import CheckIcon from '@heroicons/react/20/solid/CheckIcon.js';
import React from 'react';
import { ListBoxItem as AriaListBoxItem, Text as AriaText } from 'react-aria-components';

import type { LabelColor } from '../../tokens/colors';
import { rowStyles } from '../overlay/row';
import { UnavailableReason } from '../overlay/unavailable-reason';
import { Status, type StatusTone } from '../status/status';
import { Tag } from '../tag/tag';
import { ChoiceMark } from './choice-mark';
import { OptionLayoutContext } from './layout';
import { ListSuppressionContext } from './suppression';

interface OptionCommonProps {
  /** The value this option stands for; what `onChange` reports. */
  id: string;
  /**
   * The label. A string on purpose: it is the option's text for typeahead
   * and filtering, and what the closed picker shows.
   */
  children: string;
  /**
   * Washed out and skipped by the arrow keys. With `disabledReason` it is
   * the unavailable form instead: the option stays reachable so the reason
   * can show, and choosing it does nothing.
   */
  isDisabled?: boolean;
  /** Why the option cannot be chosen; shown as the reason pill on hover and arrow-key focus. */
  disabledReason?: string;
}

interface OptionTagProps {
  /**
   * Renders the label as a Tag in this color, in the list and in the closed
   * picker alike: for options that are labels a user attached
   * (badge-status-tag.md), never for a status the system computed. A Tag
   * option is the Tag alone: no description, glyph or avatar beside it.
   */
  color: LabelColor;
  description?: never;
  icon?: never;
  avatar?: never;
  status?: never;
  tags?: never;
}

interface OptionTextProps {
  color?: never;
  /**
   * One plain sentence of secondary context, a muted second line under the
   * label. On a record, the record's identity and summary ("Control C0001 ·
   * Background checks are performed...").
   */
  description?: string;
}

interface OptionIconProps {
  /** A decorative glyph before the label: a heroicons 24/outline component, styled by the row's recipe. */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  avatar?: never;
  /**
   * The record's state, a Status at the end of the name's line. With it (or
   * `tags`) the option is a record: an entity a search found, shown with
   * what tells it apart, the name still its accessible name.
   */
  status?: { label: string; tone: StatusTone };
  /**
   * The record's labels as Tags on a line of their own: the first four, then
   * a gray count of the rest. An empty array is a record with no labels.
   */
  tags?: ReadonlyArray<{ label: string; color: LabelColor }>;
}

interface OptionAvatarProps {
  icon?: never;
  /** An `Avatar` before the label, for an option that is a person or a team; `size="sm"`. */
  avatar: React.ReactElement;
  status?: never;
  tags?: never;
}

export type OptionProps = OptionCommonProps &
  (OptionTagProps | (OptionTextProps & (OptionIconProps | OptionAvatarProps)));

const RECORD_TAGS_SHOWN = 4;

/** What the closed picker needs of its chosen option beyond the text. */
export interface OptionMeta {
  color?: LabelColor;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  avatar?: React.ReactElement;
}

/* React Aria keeps every prop of the collected element on the collection
 * node (its own SelectValue reads `props.children` there) and filters the
 * DOM to the known attributes, so this rides along unseen; the closed
 * picker reads it back through readOptionMeta. */
const META_PROP = 'optionMeta';

export function readOptionMeta(nodeProps: unknown): OptionMeta | undefined {
  if (nodeProps == null || typeof nodeProps !== 'object') return undefined;
  return (nodeProps as Record<string, unknown>)[META_PROP] as OptionMeta | undefined;
}

/**
 * One choice in a ListBox, a Select or a Combobox: a 32px row on the recipe
 * Menu's items share, with the checkmark column every selection list keeps
 * so labels align whatever is chosen (picker.md). A record (`status` or
 * `tags`) is the taller form an entity search lists: name and status,
 * description, tags, and its checkbox (a radio in a single list) at the
 * row's end in place of the column, as every card carries it.
 */
export const Option = React.forwardRef<HTMLDivElement, OptionProps>(function Option(
  {
    id,
    children,
    description,
    icon: IconGlyph,
    avatar,
    isDisabled = false,
    disabledReason,
    color,
    status,
    tags,
  },
  forwardedRef,
) {
  /* The unavailable form is Menu's mechanism (menu.tsx): enabled for React
   * Aria so hover and arrows reach it, the list drops the selection a press
   * would cause, this restates the washed look and announces the state. */
  const unavailable = isDisabled && disabledReason != null;
  const suppression = React.useContext(ListSuppressionContext);
  const layout = React.useContext(OptionLayoutContext);
  React.useEffect(() => {
    if (!unavailable || suppression == null) return;
    return suppression.register(id);
  }, [unavailable, id, suppression]);

  const isRecord = status != null || tags != null;
  const marksAtEnd = isRecord || layout === 'cards';
  const {
    item,
    itemIcon,
    itemCheck,
    itemMark,
    itemLabel,
    itemText,
    itemDescription,
    recordText,
    recordName,
    recordDetails,
    recordDescription,
    recordTags,
  } = rowStyles({
    unavailable,
    truncate: true,
    kind: isRecord ? 'record' : avatar != null ? 'avatar' : 'line',
    surface: layout === 'cards' ? 'card' : 'row',
  });

  /* aria-disabled rides the ref callback: React Aria filters it from the
   * props and sets it only from isDisabled, which the unavailable form
   * leaves unset (menu.tsx, the proven caveat). */
  const itemRef = React.useRef<HTMLDivElement | null>(null);
  const composedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      itemRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
      if (!node) return;
      if (unavailable) node.setAttribute('aria-disabled', 'true');
      else if (!isDisabled) node.removeAttribute('aria-disabled');
    },
    [forwardedRef, unavailable, isDisabled],
  );

  const meta: OptionMeta = { color, icon: IconGlyph, avatar };

  let label: React.ReactNode;
  if (isRecord) {
    const shownTags = tags?.slice(0, RECORD_TAGS_SHOWN) ?? [];
    const moreTags = (tags?.length ?? 0) - shownTags.length;
    /* The details are one description slot around the status and the two
     * lower lines, so the option is named by its name alone and described
     * by all three. */
    label = (
      <div className={recordText()}>
        <AriaText slot="label" className={recordName()}>
          {children}
        </AriaText>
        {status || description || shownTags.length > 0 ? (
          <AriaText slot="description" elementType="div" className={recordDetails()}>
            {status ? (
              <Status tone={status.tone} size="sm">
                {status.label}
              </Status>
            ) : null}
            {description ? <span className={recordDescription()}>{description}</span> : null}
            {shownTags.length > 0 ? (
              <span className={recordTags()}>
                {shownTags.map((tag, index) => (
                  <Tag key={index} size="sm" color={tag.color}>
                    {tag.label}
                  </Tag>
                ))}
                {moreTags > 0 ? (
                  <Tag size="sm" color="gray">
                    {`+${moreTags}`}
                  </Tag>
                ) : null}
              </span>
            ) : null}
          </AriaText>
        ) : null}
      </div>
    );
  } else if (color) {
    label = (
      <span className={itemLabel()}>
        <Tag color={color}>{children}</Tag>
      </span>
    );
  } else if (description == null) {
    label = <span className={itemLabel()}>{children}</span>;
  } else {
    /* React Aria's native Text slots: the accessible name stays the label
     * and the second line is announced as the description. */
    label = (
      <div className={itemText()}>
        <AriaText slot="label" className="ck:truncate">
          {children}
        </AriaText>
        <AriaText slot="description" className={itemDescription()}>
          {description}
        </AriaText>
      </div>
    );
  }

  return (
    <AriaListBoxItem
      {...{ [META_PROP]: meta }}
      ref={composedRef}
      id={id}
      textValue={children}
      isDisabled={unavailable ? undefined : isDisabled}
      className={item()}
      data-component="option"
    >
      {({ isHovered, isFocused, isSelected, selectionMode }) => (
        <>
          {marksAtEnd ? null : (
            <CheckIcon
              aria-hidden="true"
              focusable="false"
              className={itemCheck({ selected: isSelected })}
            />
          )}
          {avatar ??
            (IconGlyph ? (
              <IconGlyph aria-hidden="true" focusable="false" className={itemIcon()} />
            ) : null)}
          {label}
          {marksAtEnd ? (
            <ChoiceMark
              isMultiple={selectionMode === 'multiple'}
              isSelected={isSelected}
              className={itemMark()}
            />
          ) : null}
          {unavailable ? (
            <UnavailableReason itemRef={itemRef} isOpen={isHovered || isFocused}>
              {disabledReason}
            </UnavailableReason>
          ) : null}
        </>
      )}
    </AriaListBoxItem>
  );
});
